import React from 'react';
import { Link } from 'react-router-dom';
import { SITE_URL } from '../config/site';

// Export the blog posts data so it can be used by BlogDetail (dateISO for schema/AEO)
export const blogPosts = [
  {
    id: 'top-100-accelerators',
    title: 'Top 100 Accelerators & Incubators 2025',
    excerpt: 'Discover the latest 2025 rankings of the most successful startup accelerators and incubators worldwide. Get exclusive insights on current funding terms, acceptance rates, and success stories.',
    date: 'January 15, 2025',
    dateISO: '2025-01-15',
    readTime: '8 min read',
    category: 'Startup Funding',
    featured: true
  },
  {
    id: 'vc-funding-challenges-2025',
    title: 'How Founders Are Navigating VC Funding Challenges in 2025',
    excerpt: 'Explore the current VC market landscape where due diligence is tougher than ever, capital efficiency is paramount, and profitability takes center stage. Learn actionable strategies for early-stage startups to demonstrate resilience and attract capital in this challenging environment.',
    date: 'January 20, 2025',
    dateISO: '2025-01-20',
    readTime: '12 min read',
    category: 'VC Funding',
    featured: false
  },
  {
    id: 'how-to-pitch-ai-startup-investors-2025',
    title: 'How to Pitch Your AI Startup to Investors in 2025: What VCs Really Want',
    excerpt: 'AI companies captured nearly half of global venture funding in 2025. Learn what investors actually look for in AI pitches: defensibility, unit economics, AI literacy, and how to stand out when every deck has an AI slide. Practical playbook for founders raising in the AI era.',
    date: 'February 10, 2025',
    dateISO: '2025-02-10',
    readTime: '10 min read',
    category: 'AI & Startups',
    featured: true
  },
  {
    id: 'defense-tech-aerospace-funding-2025',
    title: 'Defense Tech and Aerospace Startup Funding in 2025: Record Year and Where the Capital Is Going',
    excerpt: 'Defense tech and aerospace startups had a record funding year in 2025—billions flowed into autonomous systems, dual-use tech, and manufacturing scale. Discover which subsectors are hot, why VCs are betting on defense, and how founders can position for this wave.',
    date: 'February 18, 2025',
    dateISO: '2025-02-18',
    readTime: '11 min read',
    category: 'Defense & Aerospace',
    featured: false
  },
  {
    id: 'startup-fundraising-trends-2026',
    title: 'Startup Fundraising Trends in 2026: What Early-Stage Founders Need to Know',
    excerpt: 'Venture funding is slowly recovering, but capital is concentrating into fewer, larger deals. Learn how seed and Series A rounds are changing in 2026, why “high-conviction” investors matter, and what this means for your next raise.',
    date: 'March 3, 2026',
    dateISO: '2026-03-03',
    readTime: '9 min read',
    category: 'Startup Funding',
    featured: false
  },
  {
    id: 'navigating-mega-seed-rounds-2026',
    title: 'Navigating Mega Seed Rounds and Runway in 2026',
    excerpt: 'Over 40% of seed and Series A funding is going into mega-rounds in 2026, especially in AI and deep tech. Founders outside those deals can still win by designing leaner rounds, smarter runway, and tighter narratives around efficiency.',
    date: 'March 10, 2026',
    dateISO: '2026-03-10',
    readTime: '7 min read',
    category: 'Fundraising Strategy',
    featured: false
  },
  {
    id: 'agentic-ai-openclaw-moltbot-fundraising-2026',
    title: 'Agentic AI (OpenClaw, Moltbot & Co): What These “AI Operators” Mean for Fundraising in 2026',
    excerpt: 'Agentic AI tools like OpenClaw, Moltbot, and other autonomous “AI operators” are changing how founders build, run, and report on their companies. Here’s how VCs are thinking about this wave—and how to position your startup and your data room when agents are part of the story.',
    date: 'March 18, 2026',
    dateISO: '2026-03-18',
    readTime: '11 min read',
    category: 'AI & Fundraising',
    featured: false
  }
];

const Blog = () => {
  // AEO: ItemList schema for answer engines to understand blog index
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'DeckLink Blog – Startup Funding Insights & Resources',
    description: 'Insights, guides, and resources for startup founders and investors.',
    numberOfItems: blogPosts.length,
    itemListElement: blogPosts.map((post, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE_URL}/blog/${post.id}`,
      name: post.title,
      description: post.excerpt,
      datePublished: post.dateISO || post.date,
    })),
  };

  return (
    <div className="min-h-screen bg-slate-900 py-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-light text-white mb-6">
            DeckLink Blog
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Insights, guides, and resources for startup founders and investors
          </p>
        </div>

        {/* Featured Post */}
        {blogPosts.filter(post => post.featured).map((post) => (
          <div key={post.id} className="mb-16">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <span className="bg-blue-500/10 text-blue-400 text-xs font-medium px-3 py-1 rounded-full border border-blue-500/20">
                    {post.category}
                  </span>
                  <span className="text-slate-400 text-sm ml-4">{post.date}</span>
                  <span className="text-slate-400 text-sm ml-4">{post.readTime}</span>
                </div>
                <h2 className="text-3xl font-light text-white mb-6 leading-tight">
                  {post.title}
                </h2>
                <p className="text-slate-300 mb-8 text-lg leading-relaxed">
                  {post.excerpt}
                </p>
                <Link
                  to={`/blog/${post.id}`}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Read Full Article
                </Link>
              </div>
            </div>
          </div>
        ))}

        {/* All Posts */}
        <div className="grid gap-8 md:grid-cols-2">
          {blogPosts.filter(post => !post.featured).map((post) => (
            <div key={post.id} className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/30 overflow-hidden hover:bg-slate-800/50 transition-all duration-200">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <span className="bg-slate-700/50 text-slate-300 text-xs font-medium px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                  <span className="text-slate-500 text-sm ml-3">{post.date}</span>
                  <span className="text-slate-500 text-sm ml-3">{post.readTime}</span>
                </div>
                <h3 className="text-xl font-light text-white mb-4 leading-tight">
                  {post.title}
                </h3>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  {post.excerpt}
                </p>
                <Link
                  to={`/blog/${post.id}`}
                  className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  Read More →
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {blogPosts.length === 0 && (
          <div className="text-center py-16">
            <h3 className="text-lg font-light text-white mb-2">
              No blog posts yet
            </h3>
            <p className="text-slate-400">
              Check back soon for insights and guides for startup founders.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog; 